export type GuideRect = { top: number; left: number; width: number; height: number };

export type GuideLayout = {
  ring: GuideRect;
  /** Finger below the target pointing up, or above it pointing down. */
  finger: { top: number; left: number; pointing: 'up' | 'down' };
  tooltip: { top: number; left: number; width: number };
};

export const GUIDE_FINGER_SIZE = 48;
const RING_PADDING = 6;
const GUTTER = 16;
const FINGER_GAP = 4;

/** Places the ring, finger and tooltip in viewport coordinates, keeping everything on screen from 320px up. */
export function computeGuideLayout(
  target: GuideRect,
  viewport: { width: number; height: number },
  tooltipHeight: number,
): GuideLayout {
  const ring = {
    top: target.top - RING_PADDING,
    left: target.left - RING_PADDING,
    width: target.width + RING_PADDING * 2,
    height: target.height + RING_PADDING * 2,
  };
  const tooltipWidth = Math.max(Math.min(320, viewport.width - GUTTER * 2), 200);
  const needed = GUIDE_FINGER_SIZE + FINGER_GAP * 2 + tooltipHeight;
  const below = viewport.height - (ring.top + ring.height);
  const above = ring.top;
  const placeBelow = below >= needed || below >= above;

  // Visible part of the target, so a target taller than the screen still gets a finger on screen.
  const visibleTop = Math.max(ring.top, 0);
  const visibleBottom = Math.min(ring.top + ring.height, viewport.height);
  const centerX = target.left + Math.min(target.width / 2, 72);
  const fingerLeft = clamp(centerX - GUIDE_FINGER_SIZE * 0.45, 4, viewport.width - GUIDE_FINGER_SIZE - 4);
  const fingerTop = placeBelow
    ? clamp(visibleBottom + FINGER_GAP, 4, viewport.height - GUIDE_FINGER_SIZE - 4)
    : clamp(visibleTop - GUIDE_FINGER_SIZE - FINGER_GAP, 4, viewport.height - GUIDE_FINGER_SIZE - 4);

  const tooltipTop = placeBelow
    ? fingerTop + GUIDE_FINGER_SIZE + FINGER_GAP
    : fingerTop - FINGER_GAP - tooltipHeight;
  return {
    ring,
    finger: { top: fingerTop, left: fingerLeft, pointing: placeBelow ? 'up' : 'down' },
    tooltip: {
      top: clamp(tooltipTop, 8, Math.max(viewport.height - tooltipHeight - 8, 8)),
      left: clamp(target.left + target.width / 2 - tooltipWidth / 2, GUTTER, Math.max(viewport.width - GUTTER - tooltipWidth, GUTTER)),
      width: tooltipWidth,
    },
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
